import React from "react";
import { Checkbox, Col, Form, Input, Row, Select } from "antd";

import RangePickerComp from "../../../../components/common/RangePickerComp";
import SwitchComp from "../../../../components/common/SwitchComp";
import Treeselect from "../../../../components/common/Treeselect";
import ColorPickerInput from "../../Features/components/ColorPickerInput";
import {
  COLUMN_OPTIONS,
  COLUMN_TYPES,
  DEFAULT_BACKGROUND,
  DEFAULT_ITEMS_LIMIT,
  DEFAULT_PADDING,
  DEFAULT_TEXT_ALIGN,
  FREE_COLUMN_TYPES,
  HOVER_ANIMATION_TYPES,
  ITEMS_LIMIT_RULES,
  LOCAL_NAME_TYPES,
  MAX_ITEMS_LIMIT,
  MAX_PADDING,
  PADDING_RULES,
  TEXT_ALIGN_OPTIONS,
  TEXT_ALIGN_TYPES,
  TRANSPARENT_BACKGROUND,
  VALIDITY_TYPES,
  defaultPaddingFor,
  isTransparentBackground,
} from "./blockItems";

const BackgroundColorInput = ({ value, onChange }) => {
  const transparent = isTransparentBackground(value);
  const [lastColor, setLastColor] = React.useState(
    transparent ? DEFAULT_BACKGROUND : value || DEFAULT_BACKGROUND
  );

  React.useEffect(() => {
    if (value && !isTransparentBackground(value)) setLastColor(value);
  }, [value]);

  return (
    <>
      {!transparent && <ColorPickerInput value={value} onChange={onChange} />}
      <Checkbox
        className="mt-1"
        checked={transparent}
        onChange={(event) =>
          onChange(event.target.checked ? TRANSPARENT_BACKGROUND : lastColor)
        }
      >
        Transparent
      </Checkbox>
    </>
  );
};

const BlockFields = ({
  blockType,
  nameFor,
  backgroundColor,
  onBackgroundColorChange,
  textColor,
  onTextColorChange,
  showLocalName,
  onShowLocalNameChange,
  hoverAnimation = false,
  onHoverAnimationChange,
  categoryOptions = [],
  skuCount = 0,
  itemsLimit = DEFAULT_ITEMS_LIMIT,
}) => {
  const isBestInCategory = blockType === "best_in_category";
  const defaultPadding = defaultPaddingFor(backgroundColor);
  const usesAutoFill = skuCount < itemsLimit;

  return (
    <>
      <Row gutter={16}>
        <Col span={isBestInCategory ? 16 : 8}>
          <Form.Item label="Block title" name={nameFor("title")}>
            <Input placeholder="Type in" />
          </Form.Item>
        </Col>
        {!isBestInCategory && (
          <Col span={8}>
            <Form.Item label="Background color" name={nameFor("background")}>
              <BackgroundColorInput
                value={backgroundColor}
                onChange={onBackgroundColorChange}
              />
            </Form.Item>
          </Col>
        )}
        <Col span={8}>
          <Form.Item label="Text color" name={nameFor("textColor")}>
            <ColorPickerInput value={textColor} onChange={onTextColorChange} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={16}>
          <Form.Item
            label="System name"
            name={nameFor("systemName")}
            rules={[{ required: true, message: "Can't be empty" }]}
          >
            <Input placeholder="Type in" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        {[
          ["paddingTop", "Padding top (px)", defaultPadding],
          ["paddingBottom", "Padding bottom (px)", defaultPadding],
          ["paddingLeft", "Padding left (px)", DEFAULT_PADDING],
          ["paddingRight", "Padding right (px)", DEFAULT_PADDING],
        ].map(([field, label, placeholder]) => (
          <Col span={6} key={field}>
            <Form.Item label={label} name={nameFor(field)} rules={PADDING_RULES}>
              <Input
                type="number"
                min={0}
                max={MAX_PADDING}
                placeholder={String(placeholder)}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>

      {TEXT_ALIGN_TYPES.includes(blockType) && (
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Text alignment"
              name={nameFor("textAlign")}
              initialValue={DEFAULT_TEXT_ALIGN}
            >
              <Select options={TEXT_ALIGN_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {HOVER_ANIMATION_TYPES.includes(blockType) && (
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item label="Animation">
              <SwitchComp
                className=""
                checked={hoverAnimation}
                onChange={onHoverAnimationChange}
                text="Enlarge block on hover"
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={16}>
        {COLUMN_TYPES.includes(blockType) && (
          <Col span={12}>
            <Form.Item label="Number of columns" name={nameFor("columns")}>
              {FREE_COLUMN_TYPES.includes(blockType) ? (
                <Input placeholder="Type in" type="number" min={1} />
              ) : (
                <Select placeholder="Select" allowClear options={COLUMN_OPTIONS} />
              )}
            </Form.Item>
          </Col>
        )}
        {isBestInCategory && (
          <>
            <Col span={16}>
              <Form.Item
                label="Category"
                name={nameFor("category")}
                extra={
                  usesAutoFill
                    ? undefined
                    : "Not used - picked products fill the whole carousel."
                }
                rules={
                  usesAutoFill
                    ? [
                        {
                          required: true,
                          message: "Required for automatic filling",
                        },
                      ]
                    : []
                }
              >
                <Treeselect
                  options={categoryOptions}
                  placeholder="Select"
                  dropdownWidth="320px"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Product count"
                name={nameFor("itemsLimit")}
                rules={ITEMS_LIMIT_RULES}
              >
                <Input
                  type="number"
                  min={1}
                  max={MAX_ITEMS_LIMIT}
                  placeholder={String(DEFAULT_ITEMS_LIMIT)}
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      {LOCAL_NAME_TYPES.includes(blockType) && (
        <Row gutter={16} align="bottom">
          <Col span={16}>
            <Form.Item label="Local block name" name={nameFor("localName")}>
              <Input placeholder="Type in" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label=" ">
              <SwitchComp
                className=""
                checked={showLocalName}
                onChange={onShowLocalNameChange}
                text="Show"
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {VALIDITY_TYPES.includes(blockType) && (
        <Form.Item label="Validity period" name={nameFor("validity")}>
          <RangePickerComp
            showTime={{ format: "HH:mm" }}
            placeholder={["Start time", "End time"]}
            disabledDate={() => false}
          />
        </Form.Item>
      )}
    </>
  );
};

export default BlockFields;
